import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  classifyReconcileState,
  MAX_POLL_MS,
  POLL_INTERVAL_MS,
  type AnswerTranscript,
  type ReconcileState,
} from "@/lib/reconcileTranscripts";

// End-of-interview reconciliation gate, rendered at the questions_complete
// interstitial (after the last answer, before final consent). NOTIFY-ONLY: it
// classifies the current answers' transcript status (interview-session-status ->
// classifyReconcileState) and shows one of five CALM states. It NEVER offers
// re-record (no QuestionFlow changes) and NEVER traps the subject —
// needs_review / attempts_exhausted still expose Continue on the same path.
//
// The clean path (all_clear / read_failed) renders the ORIGINAL interstitial card
// UNCHANGED and reaches final consent via the exact same onContinue ->
// handleStartFinalConsent -> final_consent_recording path.

type SessionStatusForReconcile = {
  ok?: boolean;
  answer_transcripts?: AnswerTranscript[];
  answer_transcripts_error?: boolean;
};

type QuestionsCompleteGateProps = {
  resumeToken: string;
  onContinue: () => void;
};

const QuestionsCompleteGate = ({ resumeToken, onContinue }: QuestionsCompleteGateProps) => {
  // null = initial load (before the first classification resolves).
  const [state, setState] = useState<ReconcileState | null>(null);
  const mountedRef = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;
    let pollStart = 0;

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    // One fetch + classify. A whole-call failure -> read_failed (fail open; the
    // subject is never trapped by a status read we couldn't complete).
    const fetchAndClassify = async (): Promise<ReconcileState> => {
      const { data, error } = await supabase.functions.invoke<SessionStatusForReconcile>(
        "interview-session-status",
        { headers: { Authorization: `Bearer ${resumeToken}` } },
      );
      if (error || !data?.ok) return "read_failed";
      return classifyReconcileState(
        data.answer_transcripts ?? [],
        data.answer_transcripts_error ?? false,
        Date.now(),
      );
    };

    // Bounded poll: re-fetch every POLL_INTERVAL_MS while still_processing; stop
    // when the state changes away, or MAX_POLL_MS elapses (then fall through to
    // needs_review). Fully cancellable via `cancelled` + mountedRef + clearTimer.
    const tick = async () => {
      const s = await fetchAndClassify();
      if (cancelled || !mountedRef.current) return;
      if (s !== "still_processing") {
        setState(s);
        return; // resolved -> stop polling
      }
      if (Date.now() - pollStart >= MAX_POLL_MS) {
        setState("needs_review"); // grace window exhausted, still pending
        return;
      }
      timerRef.current = setTimeout(() => void tick(), POLL_INTERVAL_MS);
    };

    const run = async () => {
      const first = await fetchAndClassify();
      if (cancelled || !mountedRef.current) return;
      if (first !== "still_processing") {
        setState(first);
        return;
      }
      setState("still_processing");
      pollStart = Date.now();
      timerRef.current = setTimeout(() => void tick(), POLL_INTERVAL_MS);
    };

    void run();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      clearTimer();
    };
  }, [resumeToken]);

  // The ORIGINAL interstitial card (all_clear / read_failed), reproduced UNCHANGED
  // from the prior questions_complete render. onContinue is the same
  // handleStartFinalConsent path.
  const continueCard = (
    <div className="text-center">
      <p className="text-sm uppercase tracking-wide text-muted-foreground mb-2">
        Interview complete
      </p>
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
        Thank you — that's the last question
      </h2>
      <p className="text-muted-foreground text-lg mb-8">
        One last step: two short consent statements to read aloud.
      </p>
      <Button
        size="lg"
        onClick={onContinue}
        className="text-xl px-10 py-7 h-auto rounded-2xl"
      >
        Continue
      </Button>
    </div>
  );

  // Initial load: calm spinner, no Continue yet (state not known).
  if (state === null) {
    return (
      <div className="text-center" role="status" aria-live="polite">
        <Loader2 className="w-10 h-10 text-primary mx-auto mb-4 animate-spin" />
        <p className="text-lg text-muted-foreground">One moment…</p>
      </div>
    );
  }

  // Clean path: existing card + existing final-consent route, unchanged.
  if (state === "all_clear" || state === "read_failed") {
    return continueCard;
  }

  // Still transcribing within the grace window: calm, no alarm, Continue hidden
  // while the bounded poll runs.
  if (state === "still_processing") {
    return (
      <div className="text-center" role="status" aria-live="polite">
        <Loader2 className="w-10 h-10 text-primary mx-auto mb-4 animate-spin" />
        <p className="text-sm uppercase tracking-wide text-muted-foreground mb-2">
          Interview complete
        </p>
        <p className="text-muted-foreground text-lg">
          Just saving your last answer…
        </p>
      </div>
    );
  }

  // needs_review / attempts_exhausted: inform but never trap — Continue present on
  // the same final-consent path, no re-record UI. Calm/dignified, muted styling
  // (NOT error-red) — this is the emotional end of an interview.
  const notice =
    state === "attempts_exhausted"
      ? "We had trouble processing one of your answers. LegacyLens will follow up with you before your tribute is finalized — you can continue."
      : "One or more of your answers is still being processed. LegacyLens will review this before your tribute is finalized — you can continue.";

  return (
    <div className="text-center">
      <p className="text-sm uppercase tracking-wide text-muted-foreground mb-2">
        Interview complete
      </p>
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
        Thank you — that's the last question
      </h2>
      <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
        {notice}
      </p>
      <Button
        size="lg"
        onClick={onContinue}
        className="text-xl px-10 py-7 h-auto rounded-2xl"
      >
        Continue
      </Button>
    </div>
  );
};

export default QuestionsCompleteGate;
